'use client'

import { useEffect, useState, useRef } from 'react'
import { Star, CheckCircle } from 'lucide-react'

interface Review {
  quote: string
  author: string
  role: string
  rating?: number
  date?: string
}

interface ScrollingReviewsProps {
  reviews: Review[]
  speed?: 'slow' | 'medium' | 'fast'
  aggregateRating?: number
  totalReviews?: number
  showAggregateHeader?: boolean
}

// Stars component
function StarRating({ rating = 5, size = 16 }: { rating?: number; size?: number }) {
  return (
    <div className="flex items-center gap-1" aria-label={`${rating} out of 5 stars`} role="img">
      {[...Array(5)].map((_, i) => (
        <Star
          key={i}
          className={`animate-star-shimmer ${i < rating ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}`}
          style={{ 
            width: size, 
            height: size,
            animationDelay: `${i * 50}ms`
          }}
          aria-hidden="true"
        />
      ))}
    </div>
  )
}

// Avatar component with initials
function Avatar({ name }: { name: string }) {
  const initials = name
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)

  return (
    <div 
      className="flex-shrink-0 flex items-center justify-center w-10 h-10 rounded-full bg-[#0A2342] text-white text-sm font-semibold"
      aria-hidden="true"
    >
      {initials}
    </div>
  )
}

// Count-up animation hook
function useCountUp(end: number, duration: number, enabled: boolean) {
  const [count, setCount] = useState(0)
  const countRef = useRef(0)

  useEffect(() => {
    if (!enabled) {
      setCount(end)
      return
    }

    const startTime = Date.now()
    const timer = setInterval(() => {
      const elapsed = Date.now() - startTime
      const progress = Math.min(elapsed / duration, 1)
      const easeOutQuart = 1 - Math.pow(1 - progress, 4)
      countRef.current = easeOutQuart * end
      setCount(countRef.current)

      if (progress === 1) {
        clearInterval(timer)
      }
    }, 16)

    return () => clearInterval(timer)
  }, [end, duration, enabled])

  return count
}

export default function ScrollingReviews({ 
  reviews,
  speed = 'slow',
  aggregateRating = 4.9,
  totalReviews = 120,
  showAggregateHeader = true
}: ScrollingReviewsProps) {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false)
  const [isVisible, setIsVisible] = useState(false)
  const sectionRef = useRef<HTMLElement>(null)
  const marqueeRef = useRef<HTMLDivElement>(null)

  // Overlay state
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null)

  const animatedRating = useCountUp(aggregateRating, 600, isVisible && !prefersReducedMotion)
  const animatedCount = useCountUp(totalReviews, 600, isVisible && !prefersReducedMotion)

  useEffect(() => {
    // Check user's motion preference
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)')
    setPrefersReducedMotion(mediaQuery.matches)

    const handleChange = (e: MediaQueryListEvent) => {
      setPrefersReducedMotion(e.matches)
    }

    mediaQuery.addEventListener('change', handleChange)
    return () => mediaQuery.removeEventListener('change', handleChange)
  }, [])

  useEffect(() => {
    // Intersection observer for count-up animation
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true)
          }
        })
      },
      { threshold: 0.2 }
    )

    if (sectionRef.current) {
      observer.observe(sectionRef.current)
    }

    return () => observer.disconnect()
  }, [])

  const speedConfig = {
    slow: 50,
    medium: 35,
    fast: 20
  }

  const animationDuration = speedConfig[speed]

  // Quintuple the reviews array for truly seamless infinite loop
  // More duplicates = smoother infinite feel with no visible reset
  const duplicatedReviews = [...reviews, ...reviews, ...reviews, ...reviews, ...reviews]

  // Generate Schema.org JSON-LD
  const schemaData = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": "Spitzer Helicopters MRO Services",
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": aggregateRating.toFixed(1),
      "reviewCount": totalReviews,
      "bestRating": "5",
      "worstRating": "1"
    },
    "review": reviews.slice(0, 5).map((review, index) => ({
      "@type": "Review",
      "author": {
        "@type": "Person",
        "name": review.author
      },
      "datePublished": review.date || new Date().toISOString().split('T')[0],
      "reviewBody": review.quote,
      "reviewRating": {
        "@type": "Rating",
        "ratingValue": review.rating || 5,
        "bestRating": "5",
        "worstRating": "1"
      }
    }))
  }

  const pauseMarquee = () => {
    const marquee = marqueeRef.current
    if (marquee) marquee.style.animationPlayState = 'paused'
  }

  const resumeMarquee = () => {
    const marquee = marqueeRef.current
    if (marquee) marquee.style.animationPlayState = 'running'
  }

  useEffect(() => {
    if (expandedIndex !== null) {
      pauseMarquee()
      document.body.style.overflow = 'hidden'
    } else {
      resumeMarquee()
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [expandedIndex])

  if (prefersReducedMotion) {
    // Static grid for reduced motion
    return (
      <>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
        />
        <section 
          ref={sectionRef}
          aria-label="Customer reviews" 
          className="bg-gray-50 py-12 sm:py-16 lg:py-20 border-t border-b border-gray-200"
        >
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            {/* Aggregate Header */}
            {showAggregateHeader && (
              <div className="text-center mb-8 sm:mb-12">
                <div className="flex items-center justify-center gap-3 mb-3">
                  <Star className="w-8 h-8 fill-amber-400 text-amber-400" aria-hidden="true" />
                  <span className="text-4xl sm:text-5xl font-bold text-[#0A2342]">
                    {prefersReducedMotion ? aggregateRating.toFixed(1) : animatedRating.toFixed(1)}/5
                  </span>
                </div>
                {/* Removed blurb: from XXX+ reviews • Verified customers */}
              </div>
            )}

            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-8 sm:mb-12 text-center">
              What Our Customers Say
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {reviews.slice(0, 3).map((review, index) => (
                <figure 
                  key={index}
                  className="bg-white rounded-2xl p-6 border border-black/5 shadow-sm"
                >
                  <StarRating rating={review.rating || 5} size={14} />
                  <blockquote className="text-base leading-relaxed text-gray-700 my-4">
                    "{review.quote}"
                  </blockquote>
                  <figcaption className="flex items-center gap-3 pt-4 border-t border-gray-100">
                    <Avatar name={review.author} />
                    <div className="text-sm">
                      <div className="font-semibold text-[#0A2342] flex items-center gap-2">
                        {review.author}
                        <span className="inline-flex items-center gap-1 rounded-full border border-black/10 px-2 py-0.5 text-xs text-gray-600">
                          <CheckCircle className="w-3 h-3" aria-hidden="true" />
                          Verified
                        </span>
                      </div>
                      <div className="text-gray-600">{review.role}</div>
                    </div>
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>
      </>
    )
  }

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
      />
      <section 
        ref={sectionRef}
        aria-label="Customer reviews" 
        className="bg-gray-50 py-12 sm:py-16 lg:py-20 border-t border-b border-gray-200 overflow-hidden"
      >
        {/* Aggregate Header */}
        {showAggregateHeader && (
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center mb-8 sm:mb-12">
            <div className="flex items-center justify-center gap-3 mb-3">
              <Star className="w-8 h-8 fill-amber-400 text-amber-400" aria-hidden="true" />
              <span className="text-4xl sm:text-5xl font-bold text-[#0A2342]">
                {animatedRating.toFixed(1)}/5
              </span>
            </div>
            {/* Removed blurb: from XXX+ reviews • Verified customers */}
          </div>
        )}

        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 mb-8 sm:mb-12">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 text-center">
            What Our Customers Say
          </h2>
        </div>

        <div className="relative">
          {/* Gradient fade overlays */}
          <div className="absolute left-0 top-0 bottom-0 w-24 sm:w-32 bg-gradient-to-r from-gray-50 to-transparent z-10 pointer-events-none" />
          <div className="absolute right-0 top-0 bottom-0 w-24 sm:w-32 bg-gradient-to-l from-gray-50 to-transparent z-10 pointer-events-none" />
          
          {/* Scrolling container with pause on hover and manual horizontal scroll */}
          <div 
            className="flex items-stretch gap-6 pause-on-hover overflow-x-auto scroll-smooth"
            onMouseEnter={(e) => {
              if (expandedIndex !== null) return
              const marquee = e.currentTarget.querySelector('.animate-marquee') as HTMLElement
              if (marquee) marquee.style.animationPlayState = 'paused'
            }}
            onMouseLeave={(e) => {
              if (expandedIndex !== null) return
              const marquee = e.currentTarget.querySelector('.animate-marquee') as HTMLElement
              if (marquee) marquee.style.animationPlayState = 'running'
            }}
            onFocus={(e) => {
              if (expandedIndex !== null) return
              const marquee = e.currentTarget.querySelector('.animate-marquee') as HTMLElement
              if (marquee) marquee.style.animationPlayState = 'paused'
            }}
            onBlur={(e) => {
              if (expandedIndex !== null) return
              const marquee = e.currentTarget.querySelector('.animate-marquee') as HTMLElement
              if (marquee) marquee.style.animationPlayState = 'running'
            }}
            onPointerDown={() => {
              if (expandedIndex !== null) return
              pauseMarquee()
            }}
            onPointerUp={() => {
              if (expandedIndex !== null) return
              // Slight delay to avoid jitter when lifting finger/mouse
              setTimeout(() => resumeMarquee(), 300)
            }}
            onTouchStart={() => {
              if (expandedIndex !== null) return
              pauseMarquee()
            }}
            onTouchEnd={() => {
              if (expandedIndex !== null) return
              setTimeout(() => resumeMarquee(), 300)
            }}
          >
            <div 
              ref={marqueeRef}
              className="flex items-stretch gap-6 animate-marquee"
              style={{
                animationDuration: `${animationDuration}s`,
              }}
              aria-live="off"
            >
              {duplicatedReviews.map((review, index) => (
                <figure 
                  key={`review-${index}`}
                  className="flex-shrink-0 bg-white rounded-2xl p-5 sm:p-6 border border-black/5 shadow-sm hover:shadow-md transition-all duration-200 hover:-translate-y-0.5 w-[320px] sm:w-[400px] flex flex-col"
                  tabIndex={0}
                  role="group"
                  aria-label={`Review by ${review.author}`}
                  style={{
                    transform: prefersReducedMotion ? 'none' : undefined
                  }}
                >
                  <StarRating rating={review.rating || 5} size={14} />
                  <blockquote className="text-base md:text-[17px] text-gray-700 leading-relaxed my-4 flex-grow">
                    <span className="line-clamp-5">"{review.quote}"</span>
                  </blockquote>
                  <figcaption className="flex items-center gap-3 pt-4 border-t border-gray-100">
                    <Avatar name={review.author} />
                    <div className="text-sm flex-1">
                      <div className="font-semibold text-[#0A2342] mb-1 flex items-center gap-2 flex-wrap">
                        <span>{review.author}</span>
                        <span className="inline-flex items-center gap-1 rounded-full border border-black/10 px-2 py-0.5 text-xs text-gray-600">
                          <CheckCircle className="w-3 h-3" aria-hidden="true" />
                          Verified
                        </span>
                      </div>
                      <div className="text-gray-600 text-sm">{review.role}</div>
                    </div>
                  </figcaption>
                  <button
                    onClick={() => setExpandedIndex(index)}
                    className="mt-3 self-start text-blue-600 hover:text-blue-700 text-sm font-medium"
                    aria-label={`View full review by ${review.author}`}
                  >
                    View More
                  </button>
                </figure>
              ))}
            </div>
          </div>
        </div>

        {/* Overlay Expanded Card */}
        {expandedIndex !== null && (
          <div
            className="fixed inset-0 z-40 flex items-center justify-center bg-black/60 p-4"
            onClick={() => setExpandedIndex(null)}
            role="dialog"
            aria-modal="true"
          >
            <div
              className="max-w-2xl w-full bg-white rounded-2xl shadow-2xl border border-gray-200 p-6 sm:p-8 transform transition-all"
              onClick={(e) => e.stopPropagation()}
            >
              {(() => {
                const review = duplicatedReviews[expandedIndex]
                return (
                  <>
                    <StarRating rating={review.rating || 5} size={16} />
                    <blockquote className="text-lg text-gray-800 leading-relaxed my-6">
                      "{review.quote}"
                    </blockquote>
                    <div className="flex items-center gap-3 pt-4 border-t border-gray-100">
                      <Avatar name={review.author} />
                      <div className="text-sm">
                        <div className="font-semibold text-gray-900">{review.author}</div>
                        <div className="text-gray-600">{review.role}</div>
                      </div>
                    </div>
                    <div className="mt-6 flex justify-end">
                      <button
                        onClick={() => setExpandedIndex(null)}
                        className="inline-flex items-center rounded-md bg-blue-600 px-4 py-2 text-white text-sm font-semibold shadow-sm hover:bg-blue-700"
                        aria-label="Close full review"
                      >
                        Close
                      </button>
                    </div>
                  </>
                )
              })()}
            </div>
          </div>
        )}

        <style jsx>{`
          @keyframes marquee {
            0% {
              transform: translateX(0);
            }
            100% {
              transform: translateX(-20%);
            }
          }

          @keyframes star-shimmer {
            0% {
              opacity: 0;
              transform: scale(0.8);
            }
            50% {
              opacity: 1;
            }
            100% {
              opacity: 1;
              transform: scale(1);
            }
          }

          .animate-marquee {
            animation: marquee linear infinite;
            will-change: transform;
            /* Ensure smooth infinite loop with no visible jumps */
            backface-visibility: hidden;
            perspective: 1000px;
          }

          .animate-star-shimmer {
            animation: star-shimmer 150ms ease-out forwards;
          }

          /* Hide scrollbars for cleaner infinite scroll appearance */
          .overflow-x-auto::-webkit-scrollbar {
            display: none;
          }
          .overflow-x-auto {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }

          @media (prefers-reduced-motion: reduce) {
            .animate-marquee {
              animation: none;
            }
            .animate-star-shimmer {
              animation: none;
            }
            figure:hover {
              transform: none !important;
            }
          }
        `}</style>
      </section>
    </>
  )
}

